import { useRouterState } from '@tanstack/react-router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * **Die Seitenleiste als Schublade** ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)).
 *
 * Unterhalb von 1024 px steht die Navigation nicht mehr neben dem Inhalt, sondern darüber –
 * eingeklappt, bis jemand den Menüknopf in der Kopfzeile drückt. Der Zustand liegt hier und
 * nicht in `AppShell`, weil ihn zwei weit auseinander liegende Stellen brauchen: die
 * Kopfzeile jeder Ansicht öffnet, die Seitenleiste selbst schließt.
 *
 * Dasselbe Muster wie {@link PaletteProvider} – aus demselben Grund: ein künstliches
 * Klickereignis quer durch den Baum wäre in Tests nicht nachvollziehbar.
 *
 * ## Warum der Router hier nicht vorkommt
 *
 * Die Schublade muss sich beim Navigieren schließen – sonst bliebe sie über der Ansicht
 * stehen, die man gerade ausgewählt hat. Naheliegend wäre, den Pfad in diesem Provider zu
 * beobachten. Er steht aber im Test **außerhalb** des Routers (`test/rahmen.tsx`), und
 * `useRouterState` würde dort werfen – jede Ansicht mit Kopfzeile wäre rot.
 *
 * Deshalb macht das {@link SchliesstBeiNavigation}, eine leere Komponente, die *innerhalb*
 * des Routers steht. Der Provider bleibt damit ohne Voraussetzungen benutzbar.
 */
interface SchubladenZustand {
  offen: boolean
  oeffnen: () => void
  schliessen: () => void
}

const Kontext = createContext<SchubladenZustand | null>(null)

export function SchubladeProvider({ children }: { children: ReactNode }) {
  const [offen, setOffen] = useState(false)

  const oeffnen = useCallback(() => setOffen(true), [])
  const schliessen = useCallback(() => setOffen(false), [])

  /**
   * **Esc schließt.** Leitplanke 3 verlangt, dass alles per Tastatur erreichbar ist – und was
   * sich per Tastatur öffnen lässt, muss sich auch so wieder schließen lassen. Auf dem Telefon
   * gibt es keine Esc-Taste; dort tun es die Verdunklung und der Schließen-Knopf.
   */
  useEffect(() => {
    if (!offen) return
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false)
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [offen])

  /**
   * **Der Hintergrund scrollt nicht mit.** Ohne das wandert unter der offenen Schublade die
   * Ansicht weg, sobald der Finger die Verdunklung streift – man schließt das Menü und findet
   * eine andere Stelle der Seite vor als die, die man verlassen hat.
   */
  useEffect(() => {
    if (!offen) return
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = vorher
    }
  }, [offen])

  const wert = useMemo(() => ({ offen, oeffnen, schliessen }), [offen, oeffnen, schliessen])

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>
}

export function useSchublade(): SchubladenZustand {
  const wert = useContext(Kontext)
  if (!wert) throw new Error('useSchublade braucht einen SchubladeProvider')
  return wert
}

/**
 * Schließt die Schublade, sobald sich der Pfad ändert. Gehört **in** den Router, siehe
 * die Begründung oben.
 *
 * Nicht an die Links in der Seitenleiste gehängt: navigiert wird auch aus der ⌘K-Palette,
 * aus dem Aufgabendetail und über die Zurück-Taste des Browsers. Ein Klickhandler an einer
 * Stelle wäre eine Regel, die an drei anderen fehlt.
 */
export function SchliesstBeiNavigation() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { schliessen } = useSchublade()
  useEffect(() => schliessen(), [pathname, schliessen])
  return null
}
